import { useState, useEffect, createContext, useContext, useMemo } from 'react';
import { AuthService } from '../services/auth';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';


// Create Auth Context
const AuthContext = createContext(null);

// Custom hook to provide authentication context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  // Memoize the authService instance to prevent recreation on each render
  const authService = useMemo(() => new AuthService(), []);

  // Check auth status when component mounts
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsLoading(true);
        // Use the imported getCurrentUser directly instead of looking for it on authService
        const currentUser = await getCurrentUser();
        setIsAuthenticated(true);
        setUser(currentUser);
      } catch (error) {
        // If the error is UserUnAuthenticatedException, this is expected for unauthenticated users
        if (error.name === 'UserUnAuthenticatedException') {
          // This is the expected behavior for non-authenticated users, not an error
          setIsAuthenticated(false);
          setUser(null);
        } else {
          // Log any unexpected errors
          console.error('Unexpected auth check error:', error);
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Subscribe to auth status changes from the AuthService
    const subscription = authService.authStatus$.subscribe((status) => {
      setIsAuthenticated(status);
      // Only do a full auth check if status is true (avoid unnecessary API calls)
      if (status) {
        checkAuthStatus();
      } else {
        setUser(null);
        setIsLoading(false);
      }
    });

    // Initial auth check
    checkAuthStatus();

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [authService]);

  // Login function
  const login = async (email, password) => {
    try {
      const user = await authService.signIn(email, password);
      setIsAuthenticated(true);
      setUser(user);
      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  const silentSignOut = async () => {
    try {
      setIsLoading(true);
      setIsAuthenticated(false);
      setUser(null);
      await authService.silentSignOut();
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // First set loading to prevent any auth-dependent renders
      setIsLoading(true);
      
      // Clear auth state first
      setIsAuthenticated(false);
      setUser(null);
      
      // Then trigger the sign out
      await authService.signOut();
      
      // Navigate in the current tab
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure user is logged out even on error
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Auth context value - add setIsAuthenticated and setUser to the context
  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    setIsAuthenticated,  // Add this
    setUser,             // Add this
    silentSignOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};